import * as React from 'react'
import { Commit } from '@basehub/data/replicache/index'
import { BaseHubAvatar } from '~/components/basehub-avatar'
import { useUsernameFromId } from '~/hooks/use-username-from-id'
import { Skeleton } from '~/sotano-kit/primitives/skeleton'
import { useUserData } from '~/hooks-data/use-user-data'
import { clsx } from 'clsx'
import { SimpleTooltip } from '~/sotano-kit/primitives/tooltip'

export interface CommitDisplayProps {
  commit: Pick<Commit, 'id' | 'authorId' | 'message' | 'createdAt'>
  className?: string
  avatarSize?: 'petite' | 'small' | 'medium' | 'large'
  showMessage?: boolean
  messageClassName?: string
  truncateMessage?: boolean
}

export const CommitDisplay = ({
  commit,
  className,
  avatarSize = 'small',
  showMessage = true,
  messageClassName,
  truncateMessage = true,
}: CommitDisplayProps) => {
  const { username } = useUsernameFromId(commit.authorId)
  const user = useUserData(commit.authorId)

  if (user.isLoading) {
    return (
      <div className={clsx('flex items-center gap-2', className)}>
        <Skeleton
          height={avatarSize === 'petite' ? 16 : 24}
          width={avatarSize === 'petite' ? 16 : 24}
          className="rounded-full"
        />
        <Skeleton height={16} width={80} />
        {showMessage && <Skeleton height={16} width={120} />}
      </div>
    )
  }

  return (
    <div className={clsx('flex items-center gap-2', className)}>
      <div className="flex items-center gap-x-1">
        <BaseHubAvatar
          id={commit.authorId}
          name={username}
          size={avatarSize}
          className="shrink-0 border border-dark-border"
        />
        <span className="text-dark-bright font-medium text-xs truncate">
          {username}
        </span>
      </div>
      {showMessage && (
        <SimpleTooltip content={commit.message || 'No commit message'}>
          <span
            className={clsx(
              'text-dark-muted text-xs',
              truncateMessage && 'truncate',
              messageClassName
            )}
          >
            {commit.message || 'No commit message'}
          </span>
        </SimpleTooltip>
      )}
    </div>
  )
}
